import React, { useEffect } from "react";
import { useLangContext } from "components/shared/Lang";
import { Avatar, Badge, Button, Col, Dropdown, Layout, Row } from "antd";
import {
  UserOutlined,
  UserSwitchOutlined,
  SettingOutlined,
  PlusCircleOutlined,
  WechatOutlined,
  MessageOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import {
  privateAccountsPath,
  privateAddListingPath,
  privateChatsPath,
  privateListingsPath,
  privateSettingsPath,
} from "constants/path";
import Link from "components/shared/elements/Link";
import css from "../../css/index.module.css";
import styles from "components/ant/css/styles.module.css";
import { homeIcon, plusIcon, profileIcon, searchIcon } from "../../icons";
import { useAuthContext } from "components/shared/Auth";
import ZotzonAvatar from "components/shared/elements/images/ZotzonAvatar";

const { Footer } = Layout;

const FooterBox = ({ alwaysVisible = false }) => {
  const { t, lang } = useLangContext();
  const { data, userData } = useAuthContext();
  const [menuDocs, setMenuDocs] = React.useState({});
  const avatarSrc =
    userData?.avatar ||
    userData?.avatarUrl ||
    data?.avatar ||
    data?.avatarUrl ||
    data?.avatar_backup ||
    null;

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[FooterBox] user data", { data, userData, avatarSrc });
    }
  }, [avatarSrc, data, userData]);

  const userMenuItems = [
    {
      key: 1,
      label: (
        <>
          <PlusCircleOutlined />{" "}
          <Link href={privateAddListingPath()}>{t("addListing")}</Link>
        </>
      ),
    },
    {
      key: 2,
      label: (
        <>
          <Link href={privateChatsPath()}>
            <WechatOutlined />
            {t("Chats")}
          </Link>
        </>
      ),
    },
    {
      key: 3,
      label: (
        <Link href={privateAccountsPath()}>
          <UserSwitchOutlined /> {t("accounts")}
        </Link>
      ),
    },
    {
      key: 4,
      label: (
        <>
          <SettingOutlined />{" "}
          <Link href={privateSettingsPath()}>{t("settings")}</Link>
        </>
      ),
    },
    {
      key: 5,
      label: (
        <>
          <CopyOutlined />{" "}
          <Link href={privateListingsPath()}>{t("myAds")}</Link>
        </>
      ),
    },
    {
      key: 8,
      label: (
        <Link href={`/users/${data?.id ?? ""}`}>
          <Avatar
            icon={<UserOutlined />}
            size={16}
            style={{ marginRight: "6px" }}
          />
          {data?.firstName} {data?.lastName?.[0]}
        </Link>
      ),
    },
  ];

  useEffect(() => {
    const currentLang = lang || "ru";
    fetch(`/api/docs/menu/${currentLang}`)
      .then((response) => response.json())
      .then((menu) => {
        if (menu && typeof menu === "object" && !Array.isArray(menu)) {
          setMenuDocs(menu);
        }
      })
      .catch((error) =>
        console.error("Failed to load footer menu docs", error),
      );
  }, [lang]);

  return (
    <Footer
      className={`${css.footer} ${alwaysVisible ? css.footerAlwaysVisible : ""}`}
    >
      <Row
        className={`${css.mobFooterBtnRow} ${alwaysVisible ? css.mobFooterBtnRowAlwaysVisible : ""}`}
      >
        <Col className={css.mobFooterBtn}>
          <Button icon={homeIcon} href="/" type="link" />
        </Col>
        <Col className={css.mobFooterBtn}>
          <Button icon={searchIcon} href="/search" type="link" />
        </Col>
        <Col className={css.mobFooterBtn}>
          <Button
            icon={<MessageOutlined size={40} />}
            href={privateChatsPath()}
            type="link"
            aria-label={t("Chats")}
          />
        </Col>
        <Col className={css.mobFooterBtn}>
          <Button icon={plusIcon} href="/private/listings/add" type="link" />
        </Col>
        {data?.id ? (
          <Col className={css.mobFooterBtnAvatar}>
            <Dropdown
              trigger="click"
              placement="top"
              overlayStyle={{ width: "160px" }}
              menu={{ items: userMenuItems }}
            >
              <div>
                <Badge
                  dot={userData?.email_verified === false}
                  color="#ff4d4f"
                  offset={[-2, 2]}
                >
                  {avatarSrc && (
                    <ZotzonAvatar
                      className={styles.zotZonAvatar}
                      alt={data?.firstName}
                      src={avatarSrc}
                    />
                  )}
                  {!avatarSrc && <Avatar icon={<UserOutlined />} />}
                </Badge>
              </div>
            </Dropdown>
          </Col>
        ) : (
          <Col className={css.mobFooterBtn}>
            <Button
              icon={
                <Badge
                  dot={userData?.email_verified === false}
                  color="#ff4d4f"
                  offset={[-1, 1]}
                >
                  {profileIcon}
                </Badge>
              }
              href="/private/settings"
              type="link"
            />
          </Col>
        )}
      </Row>
      <div
        className={`${css.pcFooter} ${alwaysVisible ? css.pcFooterHidden : ""}`}
      >
        <Row className={css.pcFooterMenu}>
          {Object.keys(menuDocs).map((menuGroup, index) => {
            const items = menuDocs[menuGroup];
            return (
              <Col key={`el_${index}`} className={css.pcFooterMenuBox}>
                <div className={css.footerItemHead}>{menuGroup}</div>
                {items &&
                  items.map((doc, itemIndex) => {
                    const href =
                      doc.link || (doc.url ? `/docs/${doc.url}` : "#");
                    return (
                      <Col
                        key={`sub_el_${itemIndex}`}
                        className={css.footerItem}
                      >
                        <Link className={styles.colLink} href={href}>
                          {doc.title}
                        </Link>
                      </Col>
                    );
                  })}
              </Col>
            );
          })}
        </Row>
        <div>
          <Col className={styles.colFooterText}>{t("footerText")}</Col>
        </div>
      </div>
    </Footer>
  );
};

export default FooterBox;
